import React, { useState } from "react";
import { ExamAssignment, AssignmentForm } from "../../types";
import { useApp } from "../../context/AppContext";
import { uid, fmtDate } from "../../utils/helpers";
import PageShell from "../../components/layout/PageShell";
import GlassCard from "../../components/ui/GlassCard";
import StatCard from "../../components/ui/StatCard";
import Table, { Column } from "../../components/ui/Table";
import Modal from "../../components/ui/Modal";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import Badge from "../../components/ui/Badge";
import { Select } from "../../components/ui/FormFields";
import Button from "../../components/ui/Button";

const BLANK: AssignmentForm = { examId: "", candidateId: "", orgId: "" };

const AssignmentsPage: React.FC = () => {
  const { assignments, setAssignments, exams, users, orgs } = useApp();
  const [showModal, setShowModal] = useState(false);
  const [deleteA, setDeleteA] = useState<ExamAssignment | null>(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [form, setForm] = useState<AssignmentForm>(BLANK);

  const candidates = users.filter(u => u.role === "candidate");

  const save = () => {
    if (!form.examId || !form.candidateId) return;
    setAssignments(prev => [
      ...prev,
      {
        id: uid(),
        examId: form.examId,
        candidateId: form.candidateId,
        orgId: form.orgId,
        status: "pending",
        assignedAt: new Date().toISOString(),
        startedAt: null,
        completedAt: null
      }
    ]);
    setShowModal(false);
    setForm(BLANK);
  };

  const filtered = assignments.filter(a => statusFilter === "all" || a.status === statusFilter);

  const cols: Column<ExamAssignment>[] = [
    { key: "examId", label: "Exam", render: v => exams.find(e => e.id === v)?.title ?? v },
    {
      key: "candidateId", label: "Candidate", render: v => {
        const u = users.find(u => u.id === v);
        return u ? (
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-xs">{u.name[0]}</div>
            <span>{u.name}</span>
          </div>
        ) : v;
      }
    },
    { key: "orgId", label: "Org", render: v => orgs.find(o => o.id === v)?.name ?? v },
    { key: "status", label: "Status", render: v => <Badge text={v} /> },
    { key: "startedAt", label: "Started", render: v => fmtDate(v) },
    { key: "completedAt", label: "Completed", render: v => fmtDate(v) },
  ];

  return (
    <PageShell title="Assignments" subtitle="Assign and track exam progress" action={{ label: "Assign Exam", onClick: () => { setForm(BLANK); setShowModal(true); } }}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total" value={assignments.length} icon="📌" color="#6366f1" />
        <StatCard label="Pending" value={assignments.filter(a => a.status === "pending").length} icon="⏳" color="#f59e0b" />
        <StatCard label="In Progress" value={assignments.filter(a => a.status === "in_progress").length} icon="▶️" color="#3b82f6" />
        <StatCard label="Completed" value={assignments.filter(a => a.status === "completed").length} icon="✅" color="#10b981" />
      </div>

      <GlassCard className="p-5">
        <div className="flex gap-3 mb-4">
          <Select value={statusFilter} onChange={setStatusFilter}
            options={[{ value: "all", label: "All Status" }, { value: "pending", label: "Pending" }, { value: "in_progress", label: "In Progress" }, { value: "completed", label: "Completed" }]} />
        </div>
        <Table columns={cols} data={filtered} onDelete={a => setDeleteA(a)} />
      </GlassCard>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Assign Exam to Candidate" width="max-w-lg">
        <div className="space-y-4">
          <Select label="Exam" value={form.examId} onChange={v => setForm(f => ({ ...f, examId: v }))} required
            options={[{ value: "", label: "— Select Exam —" }, ...exams.filter(e => e.status === "active").map(e => ({ value: e.id, label: e.title }))]} />
          <Select label="Candidate" value={form.candidateId} onChange={v => setForm(f => ({ ...f, candidateId: v }))} required
            options={[{ value: "", label: "— Select Candidate —" }, ...candidates.map(u => ({ value: u.id, label: `${u.name} (${u.email})` }))]} />
          <Select label="Organization" value={form.orgId} onChange={v => setForm(f => ({ ...f, orgId: v }))} required
            options={[{ value: "", label: "— Select Org —" }, ...orgs.map(o => ({ value: o.id, label: o.name }))]} />
          <div className="flex gap-3 justify-end pt-2 border-t border-gray-100">
            <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={save}>Assign Exam</Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog isOpen={!!deleteA} onClose={() => setDeleteA(null)}
        onConfirm={() => setAssignments(prev => prev.filter(a => a.id !== deleteA?.id))}
        title="Remove Assignment" message="Remove this exam assignment?" confirmLabel="Remove" />
    </PageShell>
  );
};

export default AssignmentsPage;
